import React from 'react';
import { RouteAccess } from 'vrlcm-gui-types';


export const BASE_ROUTE = `/lcm/lcops/settings`;

export const SETTING_ROUTES = {
  SETTINGS: BASE_ROUTE,
  SYSTEM_DETAILS: `${BASE_ROUTE}/system-details`,
  LOGS: `${BASE_ROUTE}/logs`,
  SYSTEM_PATCHES: `${BASE_ROUTE}/patches`,
  PRODUCT_SUPPORT_PACK: `${BASE_ROUTE}/pspack`,
  SYSTEM_UPGRADE: `${BASE_ROUTE}/system-upgrade`,
  TIME_SETTINGS: `${BASE_ROUTE}/time-settings`,
  CHANGE_PASSWORD: `${BASE_ROUTE}/change-password`,
  CHANGE_CERTIFICATE: `${BASE_ROUTE}/change-certificate`,
  PROXY_DETAILS: `${BASE_ROUTE}/proxy`,
  OUTBOUND_NOTIFICATIONS: `${BASE_ROUTE}/outbound-notifications`,
  NTP_SERVERS: `${BASE_ROUTE}/ntp-servers`,
  SNMP: `${BASE_ROUTE}/snmp`,
  DNS: `${BASE_ROUTE}/dns`,
  MY_VMWARE: `${BASE_ROUTE}/my-vmware`,
  BINARY_MAPPING: `${BASE_ROUTE}/binary-mapping`,
  SMTP: `${BASE_ROUTE}/smtp`,
  AUTHENTICATION_PROVIDER: `${BASE_ROUTE}/authentication-provider`,
  LANGUAGE_SETTINGS: `${BASE_ROUTE}/language-settings`,
  SYSTEM_SETTINGS: `${BASE_ROUTE}/system-settings`,
  LOAD_BALANCER: `${BASE_ROUTE}/load-balancer`,
  CONFIGURE_LIFECYCLE_MINION: `${BASE_ROUTE}/configure-lifecycle-minion`
};

// If entry not present corresponding to role, default route is Setting page
export const DEFAULT_ROLE_ROUTES = {
  LOCKER_CERTIFICATE_ADMIN: BASE_ROUTE
};

const SystemDetails = React.lazy(() =>
  import(
    '../components/settings/system-administration/system-details/SystemDetails'
  )
);
const Settings = React.lazy(() => import('../components/settings/Settings'));
const Logs = React.lazy(() =>
  import('../components/settings/system-administration/Logs')
);
const SystemPatches = React.lazy(() =>
  import('../components/settings/system-administration/SystemPatches')
);
const ProductSupportPack = React.lazy(() =>
  import('../components/settings/system-administration/ProductSupportPack')
);
const SystemUpgrade = React.lazy(() =>
  import(
    '../components/settings/system-administration/system-upgrade/SystemUpgrade'
  )
);
const TimeSettings = React.lazy(() =>
  import('../components/settings/system-administration/TimeSettings')
);
const ChangePassword = React.lazy(() =>
  import('../components/settings/system-administration/ChangePassword')
);
const ChangeCertificate = React.lazy(() =>
  import(
    '../components/settings/system-administration/change-certificate/ChangeCertificate'
  )
);
const AuthenticationProvider = React.lazy(() =>
  import('../components/settings/system-administration/AuthenticationProvider')
);
const ProxyDetails = React.lazy(() =>
  import(
    '../components/settings/system-administration/proxy-configuration/Proxy'
  )
);
const OutboundNotifications = React.lazy(() =>
  import(
    '../components/settings/system-administration/outbound-notifications/OutboundNotifications'
  )
);
const LanguageSettings = React.lazy(() =>
  import(
    '../components/settings/system-administration/language-settings/LanguageSettings'
  )
);
const SystemSettings = React.lazy(() =>
  import(
    '../components/settings/system-administration/system-settings/SystemSettings'
  )
);
const LoadBalancer = React.lazy(() =>
  import(
    '../components/settings/system-administration/load-balancer/LoadBalancer'
  )
);

const ConfigureLifecycleMinion = React.lazy(() =>
  import(
    '../components/settings/system-administration/configure-lifecycle-minion/ConfigureLifecycleMinion'
  )
);
const NTPServers = React.lazy(() =>
  import('../components/settings/servers-accounts/NTPServers')
);
const SNMP = React.lazy(() =>
  import('../components/settings/servers-accounts/SNMP')
);
const DNS = React.lazy(() =>
  import('../components/settings/servers-accounts/DNS')
);
const MyVMware = React.lazy(() =>
  import('../components/settings/servers-accounts/MyVMware')
);
const BinaryMappingRoot = React.lazy(() =>
  import('../components/settings/servers-accounts/BinaryMappingRoot')
);
const SMTP = React.lazy(() =>
  import('../components/settings/servers-accounts/smtp/SMTP')
);

export const SETTING_ROUTES_COMPONENT_MAP = {
  [SETTING_ROUTES.SYSTEM_DETAILS]: SystemDetails,
  [SETTING_ROUTES.LOGS]: Logs,
  [SETTING_ROUTES.SYSTEM_PATCHES]: SystemPatches,
  [SETTING_ROUTES.PRODUCT_SUPPORT_PACK]: ProductSupportPack,
  [SETTING_ROUTES.SYSTEM_UPGRADE]: SystemUpgrade,
  [SETTING_ROUTES.TIME_SETTINGS]: TimeSettings,
  [SETTING_ROUTES.CHANGE_PASSWORD]: ChangePassword,
  [SETTING_ROUTES.CHANGE_CERTIFICATE]: ChangeCertificate,
  [SETTING_ROUTES.PROXY_DETAILS]: ProxyDetails,
  [SETTING_ROUTES.OUTBOUND_NOTIFICATIONS]: OutboundNotifications,
  [SETTING_ROUTES.NTP_SERVERS]: NTPServers,
  [SETTING_ROUTES.SNMP]: SNMP,
  [SETTING_ROUTES.DNS]: DNS,
  [SETTING_ROUTES.MY_VMWARE]: MyVMware,
  [SETTING_ROUTES.BINARY_MAPPING]: BinaryMappingRoot,
  [SETTING_ROUTES.SMTP]: SMTP,
  [SETTING_ROUTES.AUTHENTICATION_PROVIDER]: AuthenticationProvider,
  [SETTING_ROUTES.LANGUAGE_SETTINGS]: LanguageSettings,
  [SETTING_ROUTES.SYSTEM_SETTINGS]: SystemSettings,
  [SETTING_ROUTES.LOAD_BALANCER]: LoadBalancer,
  [SETTING_ROUTES.CONFIGURE_LIFECYCLE_MINION]: ConfigureLifecycleMinion,
  [SETTING_ROUTES.SETTINGS]: Settings
};

export const getAuthorizedRoutes = (
  roles: string[],
  routeAccess: RouteAccess
) => {
  const routesAuthorities = Object.entries(routeAccess);
  const authorizedRoutes = routesAuthorities.reduce<
    typeof SETTING_ROUTES_COMPONENT_MAP
  >((acc, [route, authorities]) => {
    if (
      (typeof authorities === 'string' && authorities === '*') ||
      (Array.isArray(authorities) &&
        authorities.some(a => roles.some(r => r === a)))
    ) {
      if (SETTING_ROUTES_COMPONENT_MAP[route]) {
        acc[route] = SETTING_ROUTES_COMPONENT_MAP[route];
      }
    }
    return acc;
  }, {});
  return authorizedRoutes;
};
